import { NetworkBasePopUp } from '../NetworkBasePopUp';
import { translateFn } from '../../../../utils/translator.helper';
import { htmlEncode } from '../../../../utils/escape.utils';
import { GzSuspendProtectionWebComponent } from './component/GzSuspendProtectionWebComponent';
import { GzDialog } from '../../../../ui/gzDialog/component/GzDialog';
import { FooterButton, NetworkPopUpFooterContentWebComponent } from '../NetworkPopUpFooterContentWebComponent';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { ConsoleRequest, DataProviderEntity, DataProviderType } from '../../../../utils/request';
import { GetCompanyFeaturesResponse } from '../../../../utils/response';
import { ReconfigureAgentEndpointMethod } from '../../../../network/types';
import { TaskType } from '../../../../tasks/networkTasks/types';
import { SuspendProtectionFormValue, SuspendProtectionTaskRequest } from './component/types';
import { ButtonColors, ButtonTypes } from '../../../../ui/gzButton/types';

export class SuspendProtectionPopUp extends NetworkBasePopUp<GzSuspendProtectionWebComponent> {
  protected title = translateFn("Suspend endpoint protection");
  protected transformToTaskTarget = true;
  protected extraDialogConfigs: Partial<GzDialog> = {isWindow: true};
  protected popUpFooterContent: NetworkPopUpFooterContentWebComponent;
  protected popUpFooterButtons: Partial<FooterButton>[] = [
    {
      type: ButtonTypes.FULL,
      id: 'confirmButton',
      color: ButtonColors.PRIMARY,
      label: translateFn('Suspend'),
    },
    {
      type: ButtonTypes.OUTLINE,
      id: 'closeButton',
      color: ButtonColors.SECONDARY,
      label: translateFn('Cancel'),
    },
  ];

  public static async getFeaturesByCompanyIds(targets?: Array<ProtectedEntity>) {
    if (!targets) {
      return [];
    }

    let response;

    try {
      response = await ConsoleRequest.createRequest<GetCompanyFeaturesResponse>({
        dataProviderType: DataProviderType.CORE,
        dataProviderEntity: DataProviderEntity.SYSTEM,
        endpoint: 'FeaturesManager',
        method: ReconfigureAgentEndpointMethod.GET_FEATURES_BY_COMPANY_IDS,
        payload: {companyIds: SuspendProtectionPopUp.getCompanyIdsFromTargets(targets)},
      });
    } catch (e) {
      console.error(e);
      return [];
    }

    return response.features;
  }

  public static getCompanyIdsFromTargets(targets?: Array<ProtectedEntity>) {
    if (!targets) {
      return [];
    }
    const companyIds = targets.map(entity => {
      if (entity.type === Constants.Companies.TYPE_COMPANY) {
        return entity._id;
      }

      return entity.company?.id;
    }).filter(companyId => companyId !== undefined);

    return [...new Set(companyIds)];
  }

  protected async handleConfirmButtonClicked() {
    const formValue = this.popUpContent.getFormValue();
    const payload = this.transformFormValueToConsoleRequestParams(formValue);
    await this.createTask(payload);
  }

  private transformFormValueToConsoleRequestParams(formValue: SuspendProtectionFormValue): SuspendProtectionTaskRequest {
    return {
      settings: {
        path: '',
        taskName: formValue.taskName,
        type: TaskType.TASK_TYPE_SUSPEND_PROTECTION_PARENT,
        targets: this.data.targets,
        specifics: {
          modules: formValue.modules,
          duration: formValue.duration,
          understandTheRisk: formValue.understandTheRisk,
          runScanOnResume: formValue.runScanOnResume,
        },
      },
    };
  }

  protected async customSetup(): Promise<void> {
    this.popUpFooterContent = new NetworkPopUpFooterContentWebComponent();
    this.popUpFooterButtons.map((button: Partial<FooterButton>) => {
      this.popUpFooterContent.updateFooterButton(button.id as string, button);
    });
    this.popUpContent = new GzSuspendProtectionWebComponent();
    this.popUpContent.dependencies = this.dependencies;
    this.popUpContent.endpointName = this.data.targets ? htmlEncode(this.data.targets[0].name) : "";
    this.popUpContent.popUpFooterContent = this.popUpFooterContent;
    await this.configureModulesComponent();
  }

  protected async configureModulesComponent() {
    this.popUpContent.availableFeatures = await SuspendProtectionPopUp.getFeaturesByCompanyIds(this.data.targets);
    this.popUpContent.configureModuleForm();
  }

}
